import React from 'react'
import {
    AdDefinition,
    ContentDataDefinitionLoader,
    Product,
    createCollectionAvailableEvent,
    createRegisterableComponentWithData,
} from '@news-mono/web-common'
import { CollectionRouteProps } from '../../../collections'
import {
    LoadMore,
    LoadMoreRouteProps,
} from '../../../data-controllers/LoadMore/LoadMore'
import type { SevenNewsSection } from '../../../__styling/settings/sections/sevennews'
import { ResponsiveContainer } from '../../../content/Picture/responsive'
import { ThemeMargins, sectionThemeOverrider } from '../../../__styling'
import { FourCardCollection } from './FourCardCollection'
import { FourCardCollectionSkeleton } from './FourCardCollectionSkeleton'
import { injectIpsos } from '../../../tracking'
import { FiveCardCollection } from '../FiveCardCollection'
import { AdUnitWrapper } from '../../../advertising'
import { chunkArray } from '../helpers'
import { StyledFourCardCollectionWrapper } from './FourCardCollection.styled'
import { SchemeOverrider } from '../../../themes/ThemeOverrider/SchemeOverrider'

export interface FourCardCollectionRouteProps
    extends CollectionRouteProps,
        LoadMoreRouteProps,
        ResponsiveContainer {
    sectionThemeOverride?: SevenNewsSection
    verticalGutters?: [keyof ThemeMargins, keyof ThemeMargins]
    verticalSpacing?: keyof ThemeMargins
    shouldHideTeaser?: boolean
    isLoadMoreShowPage?: boolean
}

export const fourCardCollectionAdStateDefinition: AdDefinition[] = [
    {
        id: 'leaderboard-billboard-four',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-five',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-six',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-seven',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-eight',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-nine',
        size: 'leaderboardBillboard',
    },
    {
        id: 'leaderboard-billboard-ten',
        size: 'leaderboardBillboard',
    },
    {
        id: 'mobile-mrec-four',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-five',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-six',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-seven',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-eight',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-nine',
        size: 'below768Mrec',
    },
    {
        id: 'mobile-mrec-ten',
        size: 'below768Mrec',
    },
]

export const FourCardCollectionRegistration =
    createRegisterableComponentWithData(
        'four-card-collection',
        ContentDataDefinitionLoader,
        (props: FourCardCollectionRouteProps, data, services) => {
            if (
                !data.loaded ||
                (data.loaded && data.result.publications.length <= 0)
            ) {
                return (
                    <FourCardCollectionSkeleton
                        verticalGutters={props.verticalGutters}
                        verticalSpacing={props.verticalSpacing}
                    />
                )
            }

            const adSlotIdHash: { [key: number]: string } = {
                0: 'four',
                1: 'five',
                2: 'six',
                3: 'seven',
                4: 'eight',
                5: 'nine',
                6: 'ten',
            }

            const adStateDefinition: AdDefinition[] = [
                ...services.adState.definition,
                ...fourCardCollectionAdStateDefinition,
            ]

            const adState = {
                ...services.adState,
                definition: adStateDefinition,
            }

            const dataDefinitionArgsOverride = JSON.parse(
                JSON.stringify(data.dataDefinitionArgs),
            )
            if (dataDefinitionArgsOverride.paging) {
                dataDefinitionArgsOverride.paging!.pageSize = 9
                dataDefinitionArgsOverride.paging!.pageOffset = 6
            }

            const override = sectionThemeOverrider(
                'sevennews',
                props.sectionThemeOverride ?? 'default',
            )

            return (
                <SchemeOverrider override={override}>
                    <StyledFourCardCollectionWrapper
                        isShowPage={props.isLoadMoreShowPage}
                    >
                        {props.isLoadMoreShowPage ? (
                            <LoadMore
                                dataDefinition={dataDefinitionArgsOverride}
                                services={services}
                                loadMoreEnabled={
                                    props.isLoadMoreShowPage === true
                                }
                                data={data}
                                onLoadMore={(loadMoreData) => {
                                    injectIpsos(
                                        window.location.hostname,
                                        window.location.pathname,
                                        'ajax',
                                    )
                                    services.onEvent(
                                        createCollectionAvailableEvent(
                                            loadMoreData.result.publications,
                                            'FourCardCollection',
                                            Product.SevenNews,
                                            services.onEvent,
                                        ),
                                    )
                                }}
                                render={(loadMoreData) => {
                                    const fiveAndFourCardData =
                                        loadMoreData.loaded
                                            ? chunkArray(
                                                  [
                                                      ...loadMoreData.result
                                                          .publications,
                                                  ].slice(4),
                                                  9,
                                              )
                                            : []

                                    return (
                                        <>
                                            <FourCardCollection
                                                items={
                                                    data.loaded
                                                        ? {
                                                              loaded: true,
                                                              result: data
                                                                  .result
                                                                  .publications,
                                                          }
                                                        : { loaded: false }
                                                }
                                                onEvent={services.onEvent}
                                                verticalGutters={
                                                    props.verticalGutters
                                                }
                                                verticalSpacing={
                                                    props.verticalSpacing
                                                }
                                            />
                                            {fiveAndFourCardData.length
                                                ? fiveAndFourCardData.map(
                                                      (
                                                          fiveAndFourCardDataPublications,
                                                          chunkIndex,
                                                      ) => {
                                                          const slotId =
                                                              adSlotIdHash[
                                                                  chunkIndex
                                                              ]

                                                          const fiveCardPublications =
                                                              fiveAndFourCardDataPublications.slice(
                                                                  0,
                                                                  5,
                                                              )

                                                          const fourCardPublications =
                                                              fiveAndFourCardDataPublications.slice(
                                                                  5,
                                                              )

                                                          return (
                                                              <>
                                                                  {slotId && (
                                                                      <>
                                                                          <AdUnitWrapper
                                                                              key={`adsChunkIndex${chunkIndex}`}
                                                                              adState={
                                                                                  adState
                                                                              }
                                                                              unitId={`leaderboard-billboard-${slotId}`}
                                                                              onEvent={
                                                                                  services.onEvent
                                                                              }
                                                                              hiddenUntilLoaded={
                                                                                  false
                                                                              }
                                                                              noticePosition={
                                                                                  'none'
                                                                              }
                                                                              adType={
                                                                                  'inline'
                                                                              }
                                                                          />
                                                                          <AdUnitWrapper
                                                                              key={`adsMobileChunkIndex${chunkIndex}`}
                                                                              adState={
                                                                                  adState
                                                                              }
                                                                              unitId={`mobile-mrec-${slotId}`}
                                                                              onEvent={
                                                                                  services.onEvent
                                                                              }
                                                                              hiddenUntilLoaded={
                                                                                  false
                                                                              }
                                                                              noticePosition={
                                                                                  'none'
                                                                              }
                                                                              adType={
                                                                                  'inline'
                                                                              }
                                                                          />
                                                                      </>
                                                                  )}
                                                                  <FiveCardCollection
                                                                      items={
                                                                          loadMoreData.loaded
                                                                              ? {
                                                                                    loaded: true,
                                                                                    result: fiveCardPublications,
                                                                                }
                                                                              : {
                                                                                    loaded: false,
                                                                                }
                                                                      }
                                                                      onEvent={
                                                                          services.onEvent
                                                                      }
                                                                      verticalGutters={
                                                                          props.verticalGutters
                                                                      }
                                                                      verticalSpacing={
                                                                          props.verticalSpacing
                                                                      }
                                                                  />
                                                                  <FourCardCollection
                                                                      items={
                                                                          loadMoreData.loaded
                                                                              ? {
                                                                                    loaded: true,
                                                                                    result: fourCardPublications,
                                                                                }
                                                                              : {
                                                                                    loaded: false,
                                                                                }
                                                                      }
                                                                      onEvent={
                                                                          services.onEvent
                                                                      }
                                                                      verticalGutters={
                                                                          props.verticalGutters
                                                                      }
                                                                      verticalSpacing={
                                                                          props.verticalSpacing
                                                                      }
                                                                  />
                                                              </>
                                                          )
                                                      },
                                                  )
                                                : null}
                                        </>
                                    )
                                }}
                            />
                        ) : (
                            <FourCardCollection
                                items={
                                    data.loaded
                                        ? {
                                              loaded: true,
                                              result: data.result.publications,
                                          }
                                        : { loaded: false }
                                }
                                onEvent={services.onEvent}
                                verticalGutters={props.verticalGutters}
                                verticalSpacing={props.verticalSpacing}
                                shouldHideTeaser={props.shouldHideTeaser}
                            />
                        )}{' '}
                    </StyledFourCardCollectionWrapper>
                </SchemeOverrider>
            )
        },
    )
